import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, FileText, Globe, Inbox, MousePointerClick, Plus, Rocket, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { listPagesPaged } from "@/api/pages";
import { listDomainsPaged } from "@/api/domains";
import { searchLeads } from "@/api/leads";
import { searchVisits } from "@/api/visits";
import { listDrafts } from "@/api/constructor";
import { formatRelativeTime } from "@/lib/utils";
import { DomainStatusPill } from "@/components/ui/status-pill";

function Stat({
    label,
    value,
    icon: Icon,
    to,
    loading,
}: {
    label: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    to: string;
    loading?: boolean;
}): React.ReactNode {
    return (
        <Link
            to={to}
            className="group rounded-lg border bg-card p-5 transition-colors hover:border-primary/40 hover:bg-accent/30"
            data-testid={`stat-${label.toLowerCase()}`}
        >
            <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">{label}</p>
                <Icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
            </div>
            <div className="mt-3 flex items-end justify-between">
                <p className="text-3xl font-semibold tracking-tight">
                    {loading ? <Spinner size="sm" /> : value}
                </p>
                <ArrowRight className="h-4 w-4 translate-x-0 text-muted-foreground opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
            </div>
        </Link>
    );
}

/** Statuses considered "actively provisioning" — sort to the top of Recent. */
const PROVISIONING_STATUSES = new Set(["PURCHASING", "NS_DELEGATED", "ZONE_ACTIVE", "WORKER_ATTACHED"]);

export function OverviewPage(): React.ReactNode {
    // Hit paged endpoints with size:1 so totalElements is accurate even
    // when the catalogue exceeds the default page size — the old "use
    // listPages().length" pattern silently capped at 50/200/whatever.
    const pagesQuery = useQuery({
        queryKey: ["pages", "count"],
        queryFn: () => listPagesPaged({ size: 1 }),
    });
    const domainsQuery = useQuery({
        queryKey: ["domains", "count-with-rows"],
        // Need both the total AND a few rows for the "Recent domains" card.
        queryFn: () => listDomainsPaged({ size: 50, sort: "updatedAt,desc" }),
    });
    const leadsQuery = useQuery({
        queryKey: ["leads", { page: 0, size: 5 }],
        queryFn: () => searchLeads({ page: 0, size: 5 }),
    });
    const visitsQuery = useQuery({
        queryKey: ["visits", { page: 0, size: 1 }],
        queryFn: () => searchVisits({ page: 0, size: 1 }),
    });
    const draftsQuery = useQuery({
        queryKey: ["drafts", "overview"],
        queryFn: () => listDrafts(),
    });

    const pageCount = pagesQuery.data?.totalElements ?? 0;
    const domainCount = domainsQuery.data?.totalElements ?? 0;
    const leadCount = leadsQuery.data?.totalElements ?? 0;
    const visitCount = visitsQuery.data?.totalElements ?? 0;

    const allDomains = domainsQuery.data?.content ?? [];
    // Sort: FAILED first, then provisioning, then everything else by updatedAt desc.
    const recentDomains = [...allDomains]
        .sort((a, b) => domainPriority(b) - domainPriority(a))
        .slice(0, 6);
    const failedCount = allDomains.filter((d) => d.status === "FAILED").length;
    const provisioningCount = allDomains.filter((d) => PROVISIONING_STATUSES.has(d.status)).length;

    const activeDrafts = (draftsQuery.data ?? []).filter((d) => d.status !== "discarded" && d.status !== "published").slice(0, 4);

    // "Get started" only when the platform is fundamentally empty.
    const isEmptyPlatform = !pagesQuery.isLoading && !domainsQuery.isLoading
        && pageCount === 0 && domainCount === 0;

    return (
        <>
            <PageHeader
                title="Overview"
                description="At-a-glance view of your landing pages, domains, and captured leads."
            />
            <div className="p-6 space-y-6">
                {isEmptyPlatform && <GetStartedCard />}

                {failedCount > 0 && (
                    <Link
                        to="/domains?status=FAILED"
                        className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive hover:bg-destructive/10"
                        data-testid="failed-domains-alert"
                    >
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span>
                            <strong>{failedCount} domain{failedCount === 1 ? "" : "s"} failed provisioning</strong>
                            {" "}— click to filter and investigate.
                        </span>
                        <ArrowRight className="ml-auto h-4 w-4" />
                    </Link>
                )}

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Stat label="Pages" icon={FileText} to="/pages" value={pageCount} loading={pagesQuery.isLoading} />
                    <Stat label="Domains" icon={Globe} to="/domains" value={domainCount} loading={domainsQuery.isLoading} />
                    <Stat label="Leads" icon={Inbox} to="/leads" value={leadCount} loading={leadsQuery.isLoading} />
                    <Stat label="Visits" icon={MousePointerClick} to="/visits" value={visitCount} loading={visitsQuery.isLoading} />
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Recent domains</CardTitle>
                            <CardDescription>
                                Most recently touched.
                                {provisioningCount > 0 && (
                                    <span className="ml-2 text-foreground">· {provisioningCount} provisioning</span>
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            {recentDomains.length > 0 ? (
                                <>
                                    <ul className="divide-y">
                                        {recentDomains.map((d) => (
                                            <li key={d.id}>
                                                <Link
                                                    to={`/domains/${d.id}`}
                                                    className="flex items-center justify-between gap-4 px-6 py-3 text-sm hover:bg-accent/50"
                                                >
                                                    <span className="font-medium truncate">{d.hostname}</span>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        <DomainStatusPill status={d.status} />
                                                        <span className="hidden sm:inline">{formatRelativeTime(d.updatedAt)}</span>
                                                    </div>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to="/domains" className="block border-t px-6 py-2 text-right text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground">
                                        View all domains →
                                    </Link>
                                </>
                            ) : (
                                <div className="px-6 pb-6 text-sm text-muted-foreground">
                                    {domainsQuery.isLoading ? "Loading…" : "No domains yet."}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Latest leads</CardTitle>
                            <CardDescription>Most recent form submissions.</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            {leadsQuery.data && leadsQuery.data.content.length > 0 ? (
                                <>
                                    <ul className="divide-y">
                                        {leadsQuery.data.content.map((l) => (
                                            <li key={l.id}>
                                                <Link
                                                    to={`/leads?id=${l.id}`}
                                                    className="flex items-center justify-between gap-4 px-6 py-3 text-sm hover:bg-accent/50"
                                                >
                                                    <div className="min-w-0 flex-1 truncate">
                                                        <span className="font-medium">
                                                            {String(l.fields.email ?? l.fields.phone ?? `#${l.id}`)}
                                                        </span>
                                                        {l.isPreview && <span className="ml-2 text-xs text-warning">(preview)</span>}
                                                        {l.domainHostname && (
                                                            <span className="ml-2 text-xs text-muted-foreground truncate">· {l.domainHostname}</span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatRelativeTime(l.createdAt)}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to="/leads" className="block border-t px-6 py-2 text-right text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground">
                                        View all leads →
                                    </Link>
                                </>
                            ) : (
                                <div className="px-6 pb-6 text-sm text-muted-foreground">
                                    {leadsQuery.isLoading ? "Loading…" : "No leads yet."}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {activeDrafts.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-primary" />
                                Constructor activity
                            </CardTitle>
                            <CardDescription>Drafts you or your team are still iterating on.</CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ul className="divide-y">
                                {activeDrafts.map((d) => (
                                    <li key={d.id}>
                                        <Link
                                            to={`/constructor/draft/${d.id}`}
                                            className="flex items-center justify-between gap-4 px-6 py-3 text-sm hover:bg-accent/50"
                                        >
                                            <div className="min-w-0 flex-1 truncate">
                                                <span className="font-medium">{d.name}</span>
                                                {d.ownerEmail && (
                                                    <span className="ml-2 text-xs text-muted-foreground">· by {shortOwner(d.ownerEmail)}</span>
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-foreground">{formatRelativeTime(d.updatedAt)}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/constructor" className="block border-t px-6 py-2 text-right text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground">
                                Open Constructor →
                            </Link>
                        </CardContent>
                    </Card>
                )}
            </div>
        </>
    );
}

function domainPriority(d: { status: string }): number {
    if (d.status === "FAILED") return 3;
    if (PROVISIONING_STATUSES.has(d.status)) return 2;
    return 1;
}

function shortOwner(email: string): string {
    const at = email.indexOf("@");
    return at > 0 ? email.slice(0, at) : email;
}

function GetStartedCard(): React.ReactNode {
    return (
        <Card data-testid="get-started-card" className="border-primary/30 bg-primary/5">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-primary" />
                    Get started
                </CardTitle>
                <CardDescription>
                    Three steps to a live landing page collecting leads.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <ol className="grid gap-3 sm:grid-cols-3">
                    <StartStep n={1} to="/domains/purchase" title="Buy a domain" desc="Pick a cheap TLD (.click / .xyz / .top) — the platform provisions DNS + TLS automatically." />
                    <StartStep n={2} to="/pages/new?mode=ai" title="Create a page" iconAlt={<Sparkles className="h-4 w-4 text-primary" />} desc="Use the AI Constructor to fork an existing live page, or describe what you want from scratch." />
                    <StartStep n={3} to="/domains" title="Assign + share" desc="Pick the new page version, assign it to your domain, and you're live within seconds." />
                </ol>
            </CardContent>
        </Card>
    );
}

function StartStep({ n, to, title, desc, iconAlt }: { n: number; to: string; title: string; desc: string; iconAlt?: React.ReactNode }): React.ReactNode {
    return (
        <Link to={to} className="group rounded-md border bg-background p-4 hover:border-primary/50 hover:bg-accent/30">
            <div className="flex items-center gap-2">
                {iconAlt ? iconAlt : (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">{n}</span>
                )}
                <span className="text-sm font-semibold">{title}</span>
                <Plus className="ml-auto h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{desc}</p>
        </Link>
    );
}
